import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/api';

interface FeedItem {
  id: string;
  type: 'receipt' | 'writeoff' | 'adjustment';
  typeLabel: string;
  date: string;
  createdAt?: string;
  status?: string;
  postedAtUtc?: string | null;
  postedByUsername?: string | null;
  summary: string;
  details: Array<{ name: string; quantity: number; weightType: string; unitPrice?: number }>;
  invoiceNumber?: string;
  isPaid?: boolean;
  notes?: string;
  reason?: string;
  eventOrderId?: number;
  sourceDepartment?: string;
  targetDepartment?: string;
  operationType?: string;
}

interface FeedResponse {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  items: FeedItem[];
}

const TYPE_COLOR: Record<string, string> = {
  receipt: 'var(--crm-success)',
  writeoff: 'var(--crm-danger)',
  adjustment: 'var(--crm-info)',
};
const TYPE_ICON: Record<string, string> = {
  receipt: '📥',
  writeoff: '📤',
  adjustment: '🔄',
};
const paginationButtonStyle = (disabled: boolean): React.CSSProperties => ({
  minHeight: 44,
  padding: '9px 14px',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-sm)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  background: disabled ? 'var(--crm-surface-alt)' : 'var(--crm-info-bg)',
  color: disabled ? 'var(--crm-text-muted)' : 'var(--crm-info)',
});

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
const formatQty = (quantity: number, weightType: string) => `${quantity.toFixed(2)} ${weightType}`.trim();
const formatDateTime = (value: string) => new Date(value).toLocaleString('uk-UA');
const isDraft = (status?: string) => status?.toLowerCase() === 'draft';
const parseReason = (reasonRaw: string): string => {
  const raw = reasonRaw || '';
  const separatorIndex = raw.indexOf('|');
  return stripHtml((separatorIndex < 0 ? raw : raw.slice(0, separatorIndex)).trim());
};
const parseNote = (noteRaw: string): string => {
  const raw = stripHtml(noteRaw || '');
  const separatorIndex = raw.indexOf('|');
  if (separatorIndex < 0) return raw;
  const head = raw.slice(0, separatorIndex).trim();
  const tail = raw.slice(separatorIndex + 1).trim();
  if (!tail.startsWith('{') || !tail.endsWith('}')) return raw;
  return head || '—';
};

const InventoryActivityFeedPage: React.FC = () => {
  const [data, setData] = useState<FeedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page: p, pageSize: 50 };
      if (typeFilter) params.type = typeFilter;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const res = await api.get<FeedResponse>('/activityfeed', { params });
      setData(res.data);
    } catch (e: any) {
      setError('Не вдалося завантажити стрічку подій.');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, fromDate, toDate]);

  useEffect(() => { load(1); setPage(1); }, [load]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const cardStyle = (type: string): React.CSSProperties => ({
    background: 'var(--crm-surface)',
    color: 'var(--crm-text)',
    border: '1px solid var(--crm-border)',
    borderLeft: `4px solid ${TYPE_COLOR[type] || 'var(--crm-border)'}`,
    borderRadius: 'var(--crm-radius-sm)',
    padding: '12px 16px',
    marginBottom: 8,
    boxShadow: 'var(--crm-shadow-sm)',
    cursor: 'pointer',
  });

  return (
    <div className="page-shell" style={{ maxWidth: 900 }}>
      <h2 style={{ marginTop: 0, color: 'var(--crm-text)' }}>📜 Стрічка складських операцій</h2>
      <p style={{ color: 'var(--crm-text-muted)', marginBottom: 20 }}>Всі прихідні накладні, списання та коригування в хронологічному порядку.</p>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20, alignItems: 'flex-end' }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: '7px 12px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }}>
          <option value="">Всі типи</option>
          <option value="receipt">📥 Прихідні накладні</option>
          <option value="writeoff">📤 Списання</option>
          <option value="adjustment">🔄 Коригування</option>
        </select>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: 'var(--crm-text-muted)' }}>
          Від
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: 'var(--crm-text-muted)' }}>
          До
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }} />
        </label>
        <button onClick={() => load(1)} disabled={loading}
          style={{ padding: '8px 18px', background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', cursor: 'pointer', alignSelf: 'flex-end' }}>
          {loading ? '…' : '🔄 Оновити'}
        </button>
      </div>

      {error && <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: 12, borderRadius: 'var(--crm-radius-sm)', marginBottom: 16 }}>{error}</div>}

      {data && (
        <div style={{ color: 'var(--crm-text-muted)', fontSize: 12, marginBottom: 12 }}>
          Знайдено записів: <b>{data.total}</b> | Сторінка {data.page} з {data.totalPages}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--crm-text-muted)' }}>Завантаження…</div>
      ) : (
        <>
          {(data?.items ?? []).map(item => (
            <div key={item.id} style={cardStyle(item.type)} onClick={() => toggleExpand(item.id)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{TYPE_ICON[item.type]}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: TYPE_COLOR[item.type], fontSize: 13 }}>{item.typeLabel}</span>
                    <span style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>
                      {new Date(item.date).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                    {isDraft(item.status) && <span style={{ fontSize: 11, background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', padding: '2px 7px', borderRadius: 'var(--crm-radius-sm)', fontWeight: 700 }}>ЧЕРНЕТКА · НЕ ВПЛИВАЄ НА ЗАЛИШОК</span>}
                    {item.status && !isDraft(item.status) && <span style={{ fontSize: 11, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', padding: '2px 7px', borderRadius: 'var(--crm-radius-sm)' }}>Проведено</span>}
                    {item.invoiceNumber && <span style={{ fontSize: 11, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', padding: '2px 6px', borderRadius: 'var(--crm-radius-sm)' }}>#{item.invoiceNumber}</span>}
                    {item.isPaid === false && <span style={{ fontSize: 11, background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', padding: '2px 6px', borderRadius: 'var(--crm-radius-sm)' }}>Не оплачено</span>}
                    {item.isPaid === true && <span style={{ fontSize: 11, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', padding: '2px 6px', borderRadius: 'var(--crm-radius-sm)' }}>Оплачено</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--crm-text)', marginTop: 3 }}>{item.summary}</div>
                  {isDraft(item.status) && item.createdAt && (
                    <div style={{ marginTop: 5, fontSize: 12, color: 'var(--crm-warning)', fontWeight: 600 }}>
                      Чернетка створена: {formatDateTime(item.createdAt)}
                    </div>
                  )}
                  {!isDraft(item.status) && item.postedAtUtc && (
                    <div style={{ marginTop: 5, fontSize: 12, color: 'var(--crm-text-muted)' }}>
                      Проведено: {formatDateTime(item.postedAtUtc)}{item.postedByUsername ? ` · ${item.postedByUsername}` : ''}
                    </div>
                  )}
                  {(item.reason || item.eventOrderId || item.invoiceNumber || item.notes || item.sourceDepartment || item.targetDepartment || item.operationType) && (
                    <div style={{ display: 'grid', gap: 2, marginTop: 6, fontSize: 12, color: 'var(--crm-text-muted)' }}>
                      {item.operationType && <div>Операція: {item.operationType}</div>}
                      {item.sourceDepartment && <div>Звідки: {item.sourceDepartment}</div>}
                      {item.targetDepartment && <div>Куди: {item.targetDepartment}</div>}
                      {item.reason && <div>Причина: {parseReason(item.reason) || '—'}</div>}
                      {item.eventOrderId && <div>Замовлення: #{item.eventOrderId}</div>}
                      {item.invoiceNumber && <div>Накладна: #{item.invoiceNumber}</div>}
                      {item.notes && <div>Примітка: {parseNote(item.notes)}</div>}
                    </div>
                  )}
                </div>
                <span style={{ color: 'var(--crm-text-muted)', fontSize: 16 }}>{expanded.has(item.id) ? '▲' : '▼'}</span>
              </div>

              {expanded.has(item.id) && item.details.length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--crm-border)' }}>
                  <table className="crm-table" style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--crm-surface-alt)' }}>
                        <th style={{ padding: '5px 8px', textAlign: 'left', color: 'var(--crm-text-muted)', fontWeight: 500 }}>Товар</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--crm-text-muted)', fontWeight: 500 }}>Кількість</th>
                        {item.type === 'receipt' && <th style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--crm-text-muted)', fontWeight: 500 }}>Ціна/од.</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {item.details.map((d, idx) => (
                        <tr key={idx} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                          <td style={{ padding: '4px 8px' }}>{d.name}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatQty(d.quantity, d.weightType)}</td>
                          {item.type === 'receipt' && <td style={{ padding: '4px 8px', textAlign: 'right', color: 'var(--crm-text)' }}>{d.unitPrice != null ? `${d.unitPrice.toFixed(2)} грн` : '—'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
              <button type="button" aria-label="Попередня сторінка" onClick={() => { setPage(p => p - 1); load(page - 1); }} disabled={page <= 1 || loading}
                style={paginationButtonStyle(page <= 1 || loading)}>← Назад</button>
              <span aria-live="polite" style={{ alignSelf: 'center', padding: '7px 12px', color: 'var(--crm-text-muted)', fontSize: 13 }}>{page} / {data.totalPages}</span>
              <button type="button" aria-label="Наступна сторінка" onClick={() => { setPage(p => p + 1); load(page + 1); }} disabled={page >= data.totalPages || loading}
                style={paginationButtonStyle(page >= data.totalPages || loading)}>Далі →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InventoryActivityFeedPage;
